import React from 'react';
import { Link } from 'react-router-dom';
import { FiPhone, FiArrowRight } from 'react-icons/fi';
import LocationVehiclePage from './LocationVehiclePage';

const seoConfig = {
  city: 'Weatherford',
  state: 'TX',
  slug: 'weatherford-tx',
  county: 'Parker County',
  title: 'Used Cars Weatherford TX - Pre-Owned Trucks Near Parker County',
  h1: 'Used Cars and Trucks Near Weatherford, TX',
  metaDescription: 'Used cars and trucks near Weatherford TX. 30 minutes north of Parker County. Skip the I-20 markup. Full service shop, warranty and financing on every vehicle. (817) 382-2316.',
  ogTitle: 'Used Cars Near Weatherford - Skip the I-20 Markup',
  ogDescription: 'Weatherford buyers drive 30 minutes north and save thousands. Working trucks, family SUVs, European cars — all at rural pricing.',
  ctaHeadline: 'Worth the 30-Minute Drive From Weatherford',
  ctaSubline: 'Take 180 north or 281 up from I-20. Either way, you save more than the gas costs.',
  faqs: [
    {
      q: 'How long does it take to get from Weatherford to your lot?',
      a: 'About 30 minutes. Take US-180 north to FM 920 east, or go US-281 north from I-20. Both routes avoid heavy traffic. No tolls on either.'
    },
    {
      q: 'Are your prices really lower than Weatherford dealerships?',
      a: 'Yes. Our location in Paradise means we pay a fraction of what I-20 corridor lots pay in rent and overhead. That difference goes straight to pricing. Most buyers save $2,000-$5,000 vs metro lots.'
    },
    {
      q: 'Do you carry trucks for work and towing?',
      a: 'Always. Half-tons and three-quarter-tons. Trucks that have actual tow packages, not just a hitch bolted on. We inspect every drivetrain before it hits the lot.'
    },
    {
      q: 'What warranty comes with your vehicles?',
      a: 'Every vehicle sells with a powertrain warranty. All warranty work gets done in our on-site shop — not some third-party place. Extended warranty options are available too.'
    },
    {
      q: 'Do you work on European cars?',
      a: 'Yes. BMW, Audi, Mercedes, VW, Porsche — factory-level diagnostics. Most shops in Parker County send European cars to Fort Worth. We handle them right here.'
    }
  ]
};

const WeatherfordTX = () => (
  <LocationVehiclePage config={seoConfig}>

    {/* ─── Hero ─── */}
    <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 60% 40%, rgba(217,119,6,0.15) 0%, transparent 50%)'
        }} />
      </div>
      <div className="relative container mx-auto px-4">
        <div className="max-w-4xl">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
            Used Cars and Trucks Near Weatherford, TX
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl">
            30 minutes north of Parker County. The drive pays for itself the second you see our prices.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/inventory"
              className="inline-flex items-center justify-center px-8 py-4 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-lg transition-colors"
            >
              View Inventory <FiArrowRight className="ml-2" />
            </Link>
            <a
              href="tel:+18173822316"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 border border-white/30 text-white rounded-lg font-semibold text-lg hover:bg-white/20 transition-colors"
            >
              <FiPhone className="mr-2" /> (817) 382-2316
            </a>
          </div>
        </div>
      </div>
    </section>

    {/* ─── The I-20 Problem ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">Weatherford, TX</p>
          <h2 className="text-3xl font-bold mb-6">The I-20 Strip Charges DFW Prices. We Don't.</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>Weatherford's I-20 frontage road is lined with dealerships. Every one of them pays metro rent, funds a 15-person sales floor and marks up vehicles to cover it. You're not paying for the truck. You're paying for their lease.</p>
            <p>Paradise-Performance sits 30 minutes north on a county road. Our overhead is a fraction of theirs. Same national lenders. Same vehicle sources. Lower prices because we don't burn money on a strip-mall address.</p>
            <p>Parker County's growing fast. Aledo, Hudson Oaks, Willow Park — new neighborhoods popping up every quarter. Young families moving in need vehicles on a real budget. Not the "low monthly payment" trick that stretches a loan to 84 months. Actual fair pricing.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── What Weatherford Buyers Need ─── */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2">Parker County Life</p>
          <h2 className="text-3xl font-bold mb-6">Vehicles That Match How Weatherford People Live</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-3">Working Trucks</h3>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>Half the trucks on Weatherford lots have never hauled anything heavier than grocery bags. We stock trucks that work. Tow packages, real beds, drivetrains that hold up on FM roads and job sites.</p>
                <p>Contractors, ranchers, weekend warriors — they all end up here because our trucks aren't dressed-up commuter vehicles pretending to be work rigs.</p>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-3">Family SUVs</h3>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>Weatherford ISD families need reliable transportation. Three kids, sports practice, piano lessons and a Walmart run. That's the reality. We carry SUVs and crossovers that handle it without breaking down or breaking the bank.</p>
                <p>Most priced under $20K. All warranted. All inspected in our shop before they go on the lot.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── The Shop Advantage ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6">We Sell It and We Fix It. Same Place.</h2>
          <div className="space-y-4 text-gray-700 leading-relaxed text-lg">
            <p>Most used car lots in Parker County sell you a vehicle and that's the end of the relationship. When something goes wrong, you're calling around for a mechanic. Maybe driving to Fort Worth for warranty work.</p>
            <p>We run a <Link to="/services" className="text-amber-600 hover:underline">full repair shop</Link> on the same property. Lifts. Diagnostic equipment. Mechanics who've been with us for years. Warranty work happens here. Oil changes happen here. Brakes, transmission, engine — all here.</p>
            <p>We also handle <Link to="/services/european-car-repair" className="text-amber-600 hover:underline">European cars</Link> — BMW, Audi, Mercedes, Volkswagen. Most shops in Parker County can't touch them. We have the tools and the training.</p>
          </div>
        </div>
      </div>
    </section>

    {/* ─── Parker County Neighborhoods ─── */}
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <p className="text-amber-600 font-semibold mb-2 text-center">Service Area</p>
          <h2 className="text-3xl font-bold text-center mb-10">Parker County Areas We Serve</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Hudson Oaks / Willow Park</h3>
              <p className="text-gray-600">Right off I-20. Growing suburbs with young families. Need affordable SUVs and commuter cars without the Fort Worth price tag. 25-minute drive to us.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Aledo</h3>
              <p className="text-gray-600">Aledo ISD families need multi-vehicle households. A daily driver, a weekend truck, maybe a golf cart for the neighborhood. We cover all three.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Springtown</h3>
              <p className="text-gray-600">Between Weatherford and us. Some Springtown buyers are closer to our lot than to the I-20 corridor. Working trucks and utility trailers are popular here.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Mineral Wells</h3>
              <p className="text-gray-600">West on 180. A bit further out, but buyers here save enough to make the trip twice. Trucks and ranch vehicles are the big movers.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Brock / Millsap</h3>
              <p className="text-gray-600">Rural Parker County. Acreage owners, horse property, working land. These folks need trucks and trailers that earn their keep.</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Peaster / Poolville</h3>
              <p className="text-gray-600">Closer to us than to Weatherford proper. Small-town buyers who want a small-town dealer. No sales pressure. Just vehicles and fair prices.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ─── The Drive ─── */}
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">Two Routes From Weatherford</h2>
          <div className="grid md:grid-cols-2 gap-8 text-left">
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-bold text-lg mb-3">Route 1: US-180 North</h3>
              <p className="text-gray-700">Take 180 north out of Weatherford. Pick up FM 920 east. About 30 minutes on low-traffic roads. Nice drive through the countryside.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <h3 className="font-bold text-lg mb-3">Route 2: US-281 North</h3>
              <p className="text-gray-700">Jump on I-20, take 281 north toward Decatur. Head west on FM 1655. About the same time. Slightly more highway, slightly less scenic.</p>
            </div>
          </div>
          <p className="text-gray-600 mt-6">Either way, no tolls and no DFW traffic. The 30-minute drive saves you thousands compared to anything on the Weatherford strip.</p>
        </div>
      </div>
    </section>

    {/* ─── Cross-Links ─── */}
    <section className="py-12 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Serving Parker County and Wise County</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/used-cars-bridgeport-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Bridgeport</h3>
              <p className="text-gray-500 text-sm">Lake trucks and golf carts. 6 min from us.</p>
            </Link>
            <Link to="/used-cars-decatur-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Decatur</h3>
              <p className="text-gray-500 text-sm">County seat. 10 min south on 287.</p>
            </Link>
            <Link to="/used-cars-wise-county" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Wise County</h3>
              <p className="text-gray-500 text-sm">We're based here. The whole county is home turf.</p>
            </Link>
            <Link to="/used-cars-denton-tx" className="block bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <h3 className="font-bold text-gray-900">Denton</h3>
              <p className="text-gray-500 text-sm">College town. European car focus.</p>
            </Link>
          </div>
        </div>
      </div>
    </section>

  </LocationVehiclePage>
);

export default WeatherfordTX;
